import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Plus, Phone, MessageSquare, Check, X, Edit2, Trash2 } from 'lucide-react';

const CommunicationsTab = ({ caseId, assignedTo, user }) => {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddCall, setShowAddCall] = useState(false);
  const [showAddMessage, setShowAddMessage] = useState(false);
  const [callIdInput, setCallIdInput] = useState('');
  const [messageIdInput, setMessageIdInput] = useState('');
  const [fetchingCall, setFetchingCall] = useState(false);
  const [fetchingMessage, setFetchingMessage] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState({ type: null, index: null });
  const [noteInputs, setNoteInputs] = useState({});

  const canEdit = user?.role === 'Administrator' || 
                 user?.role === 'Supervisor' || 
                 (user?.role === 'Investigator' && (assignedTo?.id === user?.id || assignedTo?._id === user?._id));

  useEffect(() => {
    fetchCaseData();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${caseId}`);
      setCaseData(response.data.data);
    } catch (error) {
      console.error('Error fetching case data:', error);
      toast.error('Failed to load case data');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchCall = async () => {
    if (!callIdInput.trim()) {
      toast.error('Please enter a Call ID');
      return;
    }

    // Check for duplicate Call ID
    const existingCalls = caseData?.calls || [];
    const callIdToCheck = callIdInput.trim();
    const isDuplicate = existingCalls.some(call => call.cdrId === callIdToCheck || call.cdrId?.toString() === callIdToCheck);
    
    if (isDuplicate) {
      toast.error('This Call ID is already added.');
      return;
    }

    try {
      setFetchingCall(true);
      const response = await api.get(`/communications/cdr/${callIdToCheck}`);
      const callData = response.data.data;

      // Add the call to the case
      await api.post(`/communications/case/${caseId}/call/cdr`, callData);
      
      toast.success('Call added successfully');
      setCallIdInput('');
      setShowAddCall(false);
      fetchCaseData();
    } catch (error) {
      console.error('Error fetching call:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch call details');
    } finally {
      setFetchingCall(false);
    }
  };

  const handleFetchMessage = async () => {
    if (!messageIdInput.trim()) {
      toast.error('Please enter a Message ID');
      return;
    }

    // Check for duplicate Message ID
    const existingMessages = caseData?.messages || [];
    const messageIdToCheck = messageIdInput.trim();
    const isDuplicate = existingMessages.some(msg => msg.messageId === messageIdToCheck || msg.messageId?.toString() === messageIdToCheck);
    
    if (isDuplicate) {
      toast.error('This Message ID is already added.');
      return;
    }

    try {
      setFetchingMessage(true);
      const response = await api.get(`/communications/message/${messageIdToCheck}`);
      const messageData = response.data.data;

      // Add the message to the case
      await api.post(`/communications/case/${caseId}/message/chat`, messageData);
      
      toast.success('Message added successfully');
      setMessageIdInput('');
      setShowAddMessage(false);
      fetchCaseData();
    } catch (error) {
      console.error('Error fetching message:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch message details');
    } finally {
      setFetchingMessage(false);
    }
  };

  const handleEditNote = (type, index, currentNote = '') => {
    setEditingNoteIndex({ type, index });
    setNoteInputs({ ...noteInputs, [`${type}-${index}`]: currentNote });
  };

  const handleSaveNote = async (type, index) => {
    const noteKey = `${type}-${index}`;
    const note = noteInputs[noteKey] || '';

    try {
      if (type === 'call') {
        await api.put(`/communications/case/${caseId}/call/${index}/note`, { note });
      } else {
        await api.put(`/communications/case/${caseId}/message/${index}/note`, { note });
      }

      toast.success('Note saved successfully');
      setEditingNoteIndex({ type: null, index: null });
      fetchCaseData();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(error.response?.data?.message || 'Failed to save note');
    }
  };

  const handleCancelNote = () => {
    setEditingNoteIndex({ type: null, index: null });
  };

  const handleDeleteNote = async (type, index) => {
    try {
      if (type === 'call') {
        await api.put(`/communications/case/${caseId}/call/${index}/note`, { note: '' });
      } else {
        await api.put(`/communications/case/${caseId}/message/${index}/note`, { note: '' });
      }

      toast.success('Note deleted successfully');
      fetchCaseData();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(error.response?.data?.message || 'Failed to delete note');
    }
  };

  const handleDeleteCall = async (index) => {
    if (!window.confirm('Are you sure you want to delete this call record? This action cannot be undone.')) {
      return;
    }

    try {
      const calls = [...(caseData?.calls || [])];
      calls.splice(index, 1);

      await api.put(`/cases/${caseId}`, { calls });
      toast.success('Call record deleted successfully');
      fetchCaseData();
    } catch (error) {
      console.error('Error deleting call:', error);
      toast.error(error.response?.data?.message || 'Failed to delete call record');
    }
  };

  const handleDeleteMessage = async (index) => {
    if (!window.confirm('Are you sure you want to delete this message record? This action cannot be undone.')) {
      return;
    }

    try {
      const messages = [...(caseData?.messages || [])];
      messages.splice(index, 1);

      await api.put(`/cases/${caseId}`, { messages });
      toast.success('Message record deleted successfully');
      fetchCaseData();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error(error.response?.data?.message || 'Failed to delete message record');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const calls = caseData?.calls || [];
  const messages = caseData?.messages || [];

  return (
    <div className="space-y-8">
      {/* Calls Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Phone className="w-5 h-5" />
            <span>Calls</span>
          </h3>
          {canEdit && !showAddCall && (
            <button
              onClick={() => setShowAddCall(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Call</span>
            </button>
          )}
        </div>

        {showAddCall && canEdit && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            {fetchingCall && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">Fetching call details...</p>
                    <p className="text-xs text-blue-700 mt-1">Please wait while we retrieve the call information</p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call ID (cdr_id)
                </label>
                <input
                  type="text"
                  value={callIdInput}
                  onChange={(e) => setCallIdInput(e.target.value)}
                  placeholder="Enter Call ID"
                  disabled={fetchingCall}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !fetchingCall) {
                      handleFetchCall();
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-2 pb-0">
                <button
                  onClick={handleFetchCall}
                  disabled={fetchingCall}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[100px] justify-center h-[42px]"
                >
                  {fetchingCall ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Fetching...</span>
                    </>
                  ) : (
                    <span>Fetch</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAddCall(false);
                    setCallIdInput('');
                  }}
                  disabled={fetchingCall}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed h-[42px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {calls.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No calls added yet</p>
            {canEdit && !showAddCall && (
              <p className="text-sm text-gray-400 mt-2">Click "Add Call" to get started</p>
            )}
          </div>
        ) : (
          <div className="table-scroll -mx-6">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Call ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Date
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Start Time
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Inmate Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Inmate ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Customer #
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Station Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Facility Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Notes
                    </th>
                    {canEdit && (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {calls.map((call, index) => {
                  const isEditing = editingNoteIndex.type === 'call' && editingNoteIndex.index === index;
                  const noteKey = `call-${index}`;
                  const currentNote = call.note || '';

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.cdrId || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(call.date)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(call.callStartTime)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.callStatus || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.inmateName || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.inmateId || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.customerNumber || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.stationName || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {call.facilityName || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 max-w-xs">
                        {isEditing ? (
                          <div className="flex items-start space-x-2">
                            <textarea
                              value={noteInputs[noteKey] !== undefined ? noteInputs[noteKey] : currentNote}
                              onChange={(e) => setNoteInputs({ ...noteInputs, [noteKey]: e.target.value })}
                              rows={2}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter note..."
                            />
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => handleSaveNote('call', index)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Save"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelNote}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <span className="flex-1 text-sm text-gray-700 line-clamp-2">
                              {currentNote || <span className="text-gray-400 italic">No note</span>}
                            </span>
                            {canEdit && (
                              <div className="flex items-center space-x-1 flex-shrink-0">
                                <button
                                  onClick={() => handleEditNote('call', index, currentNote)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Edit note"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                {currentNote && (
                                  <button
                                    onClick={() => handleDeleteNote('call', index)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete note"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDeleteCall(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete call record"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Messages Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <MessageSquare className="w-5 h-5" />
            <span>Messages</span>
          </h3>
          {canEdit && !showAddMessage && (
            <button
              onClick={() => setShowAddMessage(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Message</span>
            </button>
          )}
        </div>

        {showAddMessage && canEdit && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            {fetchingMessage && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">Fetching message details...</p>
                    <p className="text-xs text-blue-700 mt-1">Please wait while we retrieve the message information</p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message ID (chat_id)
                </label>
                <input
                  type="text"
                  value={messageIdInput}
                  onChange={(e) => setMessageIdInput(e.target.value)}
                  placeholder="Enter Message ID"
                  disabled={fetchingMessage}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !fetchingMessage) {
                      handleFetchMessage();
                    }
                  }}
                />
              </div>
              <div className="flex items-center gap-2 pb-0">
                <button
                  onClick={handleFetchMessage}
                  disabled={fetchingMessage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[100px] justify-center h-[42px]"
                >
                  {fetchingMessage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Fetching...</span>
                    </>
                  ) : (
                    <span>Fetch</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAddMessage(false);
                    setMessageIdInput('');
                  }}
                  disabled={fetchingMessage}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed h-[42px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {messages.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No messages added yet</p>
            {canEdit && !showAddMessage && (
              <p className="text-sm text-gray-400 mt-2">Click "Add Message" to get started</p>
            )}
          </div>
        ) : (
          <div className="table-scroll -mx-6">
            <div className="inline-block min-w-full align-middle">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Message ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Sender
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Receiver
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Message Text
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Date
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Facility
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Charge
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Notes
                    </th>
                    {canEdit && (
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {messages.map((message, index) => {
                  const isEditing = editingNoteIndex.type === 'message' && editingNoteIndex.index === index;
                  const noteKey = `message-${index}`;
                  const currentNote = message.note || '';

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {message.messageId || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {message.sender || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {message.receiver || 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate" title={message.messageText}>
                        {message.messageText || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(message.messageDate)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {message.facilityName || 'N/A'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {message.charge !== null && message.charge !== undefined 
                          ? `$${parseFloat(message.charge).toFixed(2)}` 
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 max-w-xs">
                        {isEditing ? (
                          <div className="flex items-start space-x-2">
                            <textarea
                              value={noteInputs[noteKey] !== undefined ? noteInputs[noteKey] : currentNote}
                              onChange={(e) => setNoteInputs({ ...noteInputs, [noteKey]: e.target.value })}
                              rows={2}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Enter note..."
                            />
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => handleSaveNote('message', index)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Save"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelNote}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <span className="flex-1 text-sm text-gray-700 line-clamp-2">
                              {currentNote || <span className="text-gray-400 italic">No note</span>}
                            </span>
                            {canEdit && (
                              <div className="flex items-center space-x-1 flex-shrink-0">
                                <button
                                  onClick={() => handleEditNote('message', index, currentNote)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Edit note"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                {currentNote && (
                                  <button
                                    onClick={() => handleDeleteNote('message', index)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete note"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleDeleteMessage(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete message record"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationsTab;

